import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsNumber } from '../../../custom-type'
import { user } from '../../auth'

// =============================================================================
// LESSON METRICS DAILY - Lookup table (generated by background function)
// Stores aggregated lesson progress data by user and date.
// Source: LearnEnrollment + LearnProgress + EarnedXp (grading = total xp earned / total xp possible).
// Used to calculate child profile lesson metrics (e.g. 30-day / 90-day aggregations).
// =============================================================================

export const LessonMetricsDaily = pgTable(
  'lesson_metrics_daily',
  {
    id: uuid().defaultRandom().primaryKey(),
    userId: text()
      .references(() => user.id, { onDelete: 'cascade' })
      .notNull(),
    date: timestamp({ mode: 'date', withTimezone: true }).notNull(), // The specific date (normalized to start of day)

    // Daily aggregated metrics from LearnProgress (and LearnEnrollment)
    lessonsCompletedCount: integer().notNull().default(0), // Count of LearnProgress with status COMPLETED completed on this day
    lessonsStartedCount: integer().notNull().default(0), // Count of lessons with progress activity (started or completed) on this day
    totalTimeSeconds: integer().notNull().default(0), // Sum of (endedAt - startedAt) in seconds for progress on this day
    enrollmentsCompletedCount: integer().notNull().default(0), // Count of LearnEnrollment with status COMPLETED and completedAt on this day

    // Per-type enrollment completion counts (subset of enrollmentsCompletedCount)
    courseEnrollmentCount: integer().notNull().default(0), // Enrollments via courseId (Tugas)
    challengeEnrollmentCount: integer().notNull().default(0), // Enrollments via challengeId (Tantangan)
    practiceEnrollmentCount: integer().notNull().default(0), // Enrollments via lessonModuleId (Latihan/Mandiri)

    // Grade achieved this day: total XP earned / total XP that could be earned (from EarnedXp linked to LearnProgress). 0..1, null when no graded lessons.
    gradeAchieved: numericAsNumber(),

    // Enrollment progress snapshot (for "Selesai belajar" = average of LearnEnrollment.progress).
    // Each started enrollment (IN_PROGRESS or COMPLETED) is anchored to exactly one day, so a
    // per-child period average = sum(enrollmentProgressSum) / sum(enrollmentProgressCount).
    enrollmentProgressSum: numericAsNumber().notNull().default(0), // Sum of LearnEnrollment.progress (0..100) anchored to this day
    enrollmentProgressCount: integer().notNull().default(0), // Number of enrollments anchored to this day (denominator)

    // Daily XP split by source, for the "Aktivitas belajar" chart.
    // assignedXp = teacher-assigned (course + challenge) XP earned this day; selfXp = self-practice (lessonModule) XP.
    // badgeXp = XP from AchievementBadge awards, which carry no LearnProgress and so belong to neither
    // enrollment bucket. It is tracked here so assignedXp + selfXp + badgeXp reconciles with the
    // "Pengalaman Belajar" per-skill total (EarnedXpDaily) — without it, badge XP appeared in that card
    // and nowhere else.
    assignedXp: numericAsNumber().notNull().default(0), // sum(EarnedXp.xpEarned) from course/challenge enrollments on this day
    selfXp: numericAsNumber().notNull().default(0), // sum(EarnedXp.xpEarned) from practice enrollments on this day
    badgeXp: numericAsNumber().notNull().default(0), // sum(EarnedXp.xpEarned) from badge awards (learnProgressId IS NULL) on this day

    // --- Membership snapshot (the "Laporan" dimension) --------------------------------------
    // The school context the learner was in AT ROLL-UP TIME, stamped by the producer on every write.
    // A report ("Laporan baca kelas 1 A") groups on these, so it keeps meaning the members of 1 A in
    // that period even after a student is moved to another class or promoted to the next grade.
    // Every column is nullable: a child who only belongs to a family organization has no school
    // context. A child can be a member of two organizations at once (family + school), which is
    // exactly why the organization is recorded here instead of being re-derived at read time.
    // NOT FOREIGN KEYS, deliberately. A snapshot must carry no referential dependency at all: even
    // `ON DELETE SET NULL` erases the value the moment a school or a class is deleted, which is the
    // history loss the snapshot exists to prevent. Plain text, resolved by the producer via
    // resolveMetricSnapshotContexts (packages/drizzle/src/queries/metric-snapshot-context.ts).
    // Because there is no FK, an id on its own is unrenderable once the row it pointed at is gone,
    // so the labels are stored beside it. They are stamped at roll-up time like everything else
    // here, which means a rebuild re-stamps them too — a renamed class rewrites its own history.
    schoolOrganizationId: text(),
    // The school's name at roll-up time, mirroring organization.name.
    schoolOrganizationName: text(),
    // The "Kelas" / rombel. Always a team of schoolOrganizationId above.
    schoolTeamId: text(),
    // The class label a report prints ("1 A"), mirroring team.name.
    schoolTeamName: text(),
    // The class avatar URL, mirroring team.image.
    schoolTeamImage: text(),
    // The "Tingkat" / grade, mirroring user.schoolClass — a plain text column, not a DB enum.
    // No separate label: it already IS the human-readable value, not an id.
    schoolClass: text(),
    // The learner's own name and avatar at roll-up time, mirroring user.name / user.image.
    // Snapshotted for the same reason as the class label: a learner can be renamed or deleted.
    studentName: text(),
    studentImage: text(),

    // Tracking fields
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_lesson_metrics_daily_user').on(t.userId),
    index('idx_lesson_metrics_daily_date').on(t.date),
    index('idx_lesson_metrics_daily_user_date').on(t.userId, t.date),
    // Reporting reads: every row a school produced in a period, then narrowed to one class.
    index('idx_lesson_metrics_daily_school_org_date').on(t.schoolOrganizationId, t.date),
    index('idx_lesson_metrics_daily_school_team_date').on(t.schoolTeamId, t.date),
    unique('uq_lesson_metrics_daily_user_date').on(t.userId, t.date),
  ],
)

export const lessonMetricsDailyRelations = relations(LessonMetricsDaily, ({ one }) => ({
  user: one(user, {
    fields: [LessonMetricsDaily.userId],
    references: [user.id],
  }),
}))

export const InsertLessonMetricsDailySchema = createInsertSchema(LessonMetricsDaily).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateLessonMetricsDailySchema = createUpdateSchema(LessonMetricsDaily).partial().omit({
  id: true,
  userId: true,
  date: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectLessonMetricsDailySchema = createSelectSchema(LessonMetricsDaily)

export type InsertLessonMetricsDaily = typeof LessonMetricsDaily.$inferInsert
export type UpdateLessonMetricsDaily = z.infer<typeof UpdateLessonMetricsDailySchema>
export type SelectLessonMetricsDaily = typeof LessonMetricsDaily.$inferSelect
